import React, {useCallback, useEffect, useState} from 'react';
import {Button, Card, Flex, message, Popconfirm, Space, Table, Tag, Typography} from 'antd';
import {DeleteOutlined, DownloadOutlined, ReloadOutlined, UploadOutlined} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import {deleteProjectWordBatch, getProjectWordList, safeUpsertProjectWordBatch} from '../api/projectWordApi';
import ExcelUploadModal from './ExcelUploadModal';
import {useParams} from 'react-router-dom';

const { Text } = Typography;

/**
 * ProjectWordManagement
 * 다운로드 양식 그대로 업로드 & uuid 기준 업서트
 */
function ProjectWordManagement({ pjtId: propProjectId }) {
  const params = useParams?.() || {};
  const effectivePjtId = propProjectId ?? params.pjtId;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadStats, setUploadStats] = useState(null);

  const columns = [
    { title: 'UUID', dataIndex: 'uuid', key: 'uuid', width: 220, ellipsis: true, sorter: (a, b) => (a.uuid || '').localeCompare(b.uuid || '') },
    { title: '한글명', dataIndex: 'name', key: 'name', width: 160, sorter: (a, b) => (a.name || '').localeCompare(b.name || '') },
    { title: '영문명', dataIndex: 'engName', key: 'engName', width: 160, sorter: (a, b) => (a.engName || '').localeCompare(b.engName || '') },
    { title: '설명', dataIndex: 'description', key: 'description', ellipsis: true, sorter: (a, b) => (a.description || '').localeCompare(b.description || '') },
    { title: '상태', key: 'status', width: 90, render: () => <Tag color="blue">사용</Tag> },
  ];

  const fetchList = useCallback(async () => {
    if (!effectivePjtId) return;
    setLoading(true);
    try {
      const list = await getProjectWordList(effectivePjtId);
      setData(Array.isArray(list) ? list : []);
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [effectivePjtId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDownloadTemplate = () => {
    const header = ['uuid','name','engName','description'];
    const sample = [['11111111-1111-1111-1111-111111111111','샘플단어','SampleWord','설명 예시']];
    const ws = XLSX.utils.aoa_to_sheet([header, ...sample]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ProjectWords');
    XLSX.writeFile(wb, `project_words_template_${effectivePjtId}.xlsx`);
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      await deleteProjectWordBatch(effectivePjtId, selectedRowKeys);
      message.success(`${selectedRowKeys.length}건 삭제 완료`);
      setSelectedRowKeys([]);
      fetchList();
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
    }
  };

  const uploadApi = async (excelRows) => {
    const rows = excelRows.map(r => ({
      uuid: r.uuid || undefined,
      name: r.name?.trim?.() || '',
      engName: r.engName?.trim?.() || '',
      description: r.description || '',
    }));
    const result = await safeUpsertProjectWordBatch(effectivePjtId, rows);
    setUploadStats(result);
    await fetchList();
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card size="small" style={{ padding: 12 }}>
        <Flex gap={8} wrap="wrap">
          <Button icon={<ReloadOutlined />} onClick={fetchList} disabled={loading}>새로고침</Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>양식 다운로드</Button>
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>엑셀 업로드</Button>
          <Popconfirm title="선택 항목 삭제" description="정말 삭제하시겠습니까?" onConfirm={handleDeleteSelected} okText="삭제" cancelText="취소" disabled={selectedRowKeys.length === 0}>
            <Button danger icon={<DeleteOutlined />} disabled={selectedRowKeys.length === 0}>선택 삭제</Button>
          </Popconfirm>
          {uploadStats && (
            <Text type="secondary">
              업로드 결과: serverUpsert={String(uploadStats.serverUsed)} / updated={uploadStats.updated} / created={uploadStats.created} / errors={uploadStats.errors.length}
            </Text>
          )}
        </Flex>
      </Card>
      <Card title={`프로젝트 단어 (${data.length})`} size="small">
        <Table
          rowKey={row => row.uuid || row.id || row.pjtWordId || row.key}
          loading={loading}
          dataSource={data}
          columns={columns}
          size="middle"
          pagination={{ pageSize: 20 }}
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        />
      </Card>

      <ExcelUploadModal
        isOpen={uploadOpen}
        onClose={() => { setUploadOpen(false); }}
        onSuccess={() => {}}
        agGridColumnDefs={[
          { headerName: 'UUID', field: 'uuid', editable: true, width: 220 },
          { headerName: '한글명', field: 'name', editable: true },
          { headerName: '영문명', field: 'engName', editable: true },
          { headerName: '설명', field: 'description', editable: true, flex: 1 },
        ]}
        agGridDefaultColDef={{ resizable: true }}
        fieldMapping={['uuid','name','engName','description']}
        uploadApi={uploadApi}
        excelGuide={`1) 양식 첫 행은 헤더입니다.\n2) uuid 값이 기존과 동일하면 업데이트, 비어있거나 신규 uuid 는 새로 생성됩니다.\n3) uuid 미입력 시 서버에서 생성(지원 시) 또는 업데이트 불가.`}
        rowKey="key"
      />
    </Space>
  );
}

export default ProjectWordManagement;
